import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Leaf, Award, BookOpen, ArrowRight } from 'lucide-react';

const UMass: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "UMass Amherst";

    return (
        <div className="min-h-screen bg-white font-sans text-[#333] selection:bg-[#881c1c] selection:text-white">
            {/* Top Bar - Maroon */}
            <div className="bg-[#881c1c] text-white py-1 px-6 flex justify-end gap-6 text-[11px] font-bold uppercase tracking-wider">
                <a href="#" className="hover:underline">Navigate</a>
                <a href="#" className="hover:underline">Spire</a>
                <a href="#" className="hover:underline">People Finder</a>
            </div>

            {/* Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 border-b border-gray-200">
                <div className="max-w-[1240px] mx-auto flex justify-between items-center">
                    <div className="flex flex-col">
                        <span className="font-serif text-3xl font-bold text-[#881c1c] tracking-tight leading-none">{schoolName.split(' ')[0]}</span>
                        <span className="font-serif text-3xl font-bold text-[#333] tracking-tight leading-none">{schoolName.split(' ').slice(1).join(' ')}</span>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#333] text-sm uppercase tracking-wide">
                        <a href="#" className="hover:text-[#881c1c] transition-colors relative group">
                            Admissions
                             <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-[#881c1c] transition-all group-hover:w-full"></span>
                        </a>
                        <a href="#" className="hover:text-[#881c1c] transition-colors relative group">
                            Academics
                             <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-[#881c1c] transition-all group-hover:w-full"></span>
                        </a>
                        <a href="#" className="hover:text-[#881c1c] transition-colors relative group">
                            Campus Life
                             <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-[#881c1c] transition-all group-hover:w-full"></span>
                        </a>
                        <a href="#" className="hover:text-[#881c1c] transition-colors relative group">
                            Research
                             <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-[#881c1c] transition-all group-hover:w-full"></span>
                        </a>
                        <a href="#" className="hover:text-[#881c1c] transition-colors relative group">
                            About
                             <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-[#881c1c] transition-all group-hover:w-full"></span>
                        </a>
                    </div>
                    
                    <div className="flex gap-4 items-center">
                         <div className="bg-[#881c1c] text-white p-2 rounded-sm cursor-pointer hover:bg-[#601010]">
                            <Search size={18} />
                         </div>
                         <Menu className="cursor-pointer lg:hidden" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - Be Revolutionary */}
            <header className="relative h-[650px]">
                <img 
                    src="https://images.unsplash.com/photo-1541339907198-e08756dedf3f?q=80&w=2070&auto=format&fit=crop" 
                    alt="UMass Amherst Campus" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                 {/* Dark Overlay */}
                <div className="absolute inset-0 bg-black/40"></div>

                <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full px-8 md:px-12">
                     <div className="max-w-3xl mx-auto text-center text-white">
                         <h1 className="text-5xl md:text-8xl font-serif font-bold mb-6 italic leading-none">
                             Be Revolutionary.
                         </h1>
                         <p className="text-xl md:text-2xl mb-10 font-bold drop-shadow-md">
                             At {schoolName}, we welcome the brilliant, the curious, and the bold.
                         </p>
                         <Button className="bg-[#881c1c] hover:bg-[#601010] text-white font-bold uppercase tracking-widest px-10 h-14 rounded-sm border-2 border-white/20 shadow-lg">
                             Apply Now
                         </Button>
                     </div>
                </div>
            </header>

            {/* Stats / Distinction */}
            <section className="bg-[#f2f2f2] py-24 px-6 md:px-12">
                <div className="max-w-[1240px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                     <div className="bg-white p-8 shadow-sm rounded-lg hover:-translate-y-2 transition-transform duration-300">
                         <Award size={48} className="text-[#881c1c] mx-auto mb-4" />
                         <h3 className="text-2xl font-bold mb-2 text-[#333]">#26</h3>
                         <p className="text-gray-600">Top Public University (US News)</p>
                     </div>
                      <div className="bg-white p-8 shadow-sm rounded-lg hover:-translate-y-2 transition-transform duration-300">
                         <Leaf size={48} className="text-[#881c1c] mx-auto mb-4" />
                         <h3 className="text-2xl font-bold mb-2 text-[#333]">Green</h3>
                         <p className="text-gray-600">Leader in Sustainability & Food Science</p>
                     </div>
                      <div className="bg-white p-8 shadow-sm rounded-lg hover:-translate-y-2 transition-transform duration-300">
                         <BookOpen size={48} className="text-[#881c1c] mx-auto mb-4" />
                         <h3 className="text-2xl font-bold mb-2 text-[#333]">R1</h3>
                         <p className="text-gray-600">Top-Tier Research Institution</p>
                     </div>
                </div>
            </section>

             {/* News Blocks */}
             <section className="py-24 px-6 md:px-12">
                 <div className="max-w-[1240px] mx-auto">
                     <div className="flex justify-between items-end mb-12 border-b-2 border-gray-200 pb-4">
                         <h2 className="text-3xl font-serif font-bold text-[#333]">Latest News</h2>
                         <a href="#" className="text-[#881c1c] font-bold uppercase text-xs hover:underline mb-2">{schoolName.split(' ')[0]} News & Views</a>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                         {[
                             "Computer science researchers awarded NSF grant for AI safety",
                             "School of Management rises in rankings",
                             "Campus Dining named #1 in the nation for 7th year",
                             "New student recreation center opens on campus"
                         ].map((title, i) => (
                             <div key={i} className="group cursor-pointer">
                                 <div className="h-48 bg-gray-100 mb-4 overflow-hidden rounded-md">
                                     <img src={`https://source.unsplash.com/random/400x300?sig=${i+70}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" alt="News"/>
                                 </div>
                                 <h3 className="text-lg font-bold text-[#333] group-hover:text-[#881c1c] transition-colors mb-2 leading-snug">{title}</h3>
                             </div>
                         ))}
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#222] text-white py-16 px-6 md:px-12">
                  <div className="max-w-[1240px] mx-auto flex flex-col md:flex-row justify-between gap-12 font-sans text-sm">
                      <div className="max-w-xs">
                           <span className="font-serif font-bold text-2xl mb-6 block">{schoolName}</span>
                          <p className="text-gray-400 mb-6">
                              Amherst, MA 01003 <br/>
                              (413) 545-0111
                          </p>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 font-bold text-gray-400">
                          <ul className="space-y-3 hover:[&_a]:text-[#881c1c]">
                              <li><a href="#">Site Map</a></li>
                              <li><a href="#">Accessibility</a></li>
                              <li><a href="#">Terms of Use</a></li>
                              <li><a href="#">Privacy</a></li>
                          </ul>
                          <ul className="space-y-3 hover:[&_a]:text-[#881c1c]">
                               <li><a href="#">Give</a></li>
                              <li><a href="#">Contact</a></li>
                              <li><a href="#">Apply</a></li>
                          </ul>
                      </div>
                  </div>
                  <div className="max-w-[1240px] mx-auto mt-16 text-center text-xs text-gray-500 border-t border-gray-800 pt-8">
                      © 2026 {schoolName}
                  </div>
             </footer>
        </div>
    );
};

export default UMass;
